import { useEffect, useState, useContext, useMemo } from 'react'
import { ApprovalStatus, kuiperClassNames } from '@opallabs/kuiper'
import { ChannelIcon } from '@opallabs/satellite'
import CardContext from '../../../../../../cardContext'
import { useWorkspaceServiceIconMap } from '../../../../../../queries/useSystemServices'
import { WeekViewGlobalContext } from '../../../../../../weekViewGlobalContext'
import { updateContentFromDrop } from '../updateContentFromDrop'

import '../Default/styles.scss'

type GhostContentListItemProps = {
  content: Record<string, any>
  index: number
  moment: Record<string, any>
  onDragRestrictedContent: (arg: boolean) => void
}

const GhostContentListItem: React.VFC<GhostContentListItemProps> = ({
  content,
  index,
  moment,
  onDragRestrictedContent,
}) => {
  const [contentName, setContentName] = useState(content.name ?? '')
  const [dragCount, setDragCount] = useState(0)
  const [hasPlaceholder, setHasPlaceholder] = useState(false)

  const { onMoveContent, onReorderContent } = useContext(CardContext) ?? {}
  const { currentBrandId } = useContext(WeekViewGlobalContext)

  const { data: serviceIconMap } = useWorkspaceServiceIconMap(currentBrandId)

  const serviceIcon = useMemo(
    () => serviceIconMap?.[content.service.id],
    [content.service.id, serviceIconMap]
  )

  useEffect(() => {
    const callback = () => onDragRestrictedContent(false)

    document.addEventListener('mouseup', callback)
    return () => document.removeEventListener('mouseup', callback)
  }, [onDragRestrictedContent])

  useEffect(() => {
    setContentName(content.name)
  }, [content.name])

  const resetDragStates = () => {
    setDragCount(0)
    setHasPlaceholder(false)
  }

  const onMouseDown = () => {
    onDragRestrictedContent(true)
  }

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    setDragCount(dragCount + 1)
    setHasPlaceholder(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragCount(dragCount - 1)
    if (dragCount - 1 === 0) {
      setHasPlaceholder(false)
    }
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()

    updateContentFromDrop({
      contentIndex: index,
      dropEvent: e,
      moment,
      onMoveContent,
      onReorderContent,
      resetDragStates,
    })
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={kuiperClassNames('contentListItem', 'isGhost', {
        hasPlaceholder,
      })}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseDown}
      onDragOver={onDragOver}
      draggable={false}
    >
      <div className={kuiperClassNames('itemInternals')}>
        {serviceIcon && (
          <ChannelIcon
            iconSize={12}
            imageSrc={serviceIcon.assetSrc}
            channelColor={serviceIcon.color}
            channelName={serviceIcon.name}
            channelIconName={serviceIcon.iconName ?? undefined}
            channelSystemName={serviceIcon.systemName}
            privateForUser={true}
          />
        )}
        <div className={kuiperClassNames('contentListItem-title')}>
          {contentName}
        </div>
        <ApprovalStatus approvalStatuses={['draft']} hideDeliveryRing />
      </div>
      <div className={kuiperClassNames('contentPlaceholder')} />
    </div>
  )
}

export default GhostContentListItem
